import { useCallback, useState } from "react"
import { useSearchParams } from "react-router"
import { useTranslation } from "react-i18next"

import { hasPermission } from "@/lib/utils"
import { Permission, Workspace } from "@/types"
import { type WorkspacePaymentRequestListFilters } from "@/hooks/api"

import WorkspacePaymentGatewaySection from "./WorkspacePaymentGatewaySection"
import WorkspacePaymentRequestDetail from "./WorkspacePaymentRequestDetail"
import WorkspacePaymentRequestList from "./WorkspacePaymentRequestList"

export interface WorkspacePaymentsTabProps {
  workspace: Workspace
}

const WorkspacePaymentsTab = ({ workspace }: WorkspacePaymentsTabProps) => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<WorkspacePaymentRequestListFilters>({
    agent_id: "all",
    created_from: "",
    created_to: "",
    query: "",
    include_guest_requests: true,
  })

  const canManagePaymentRequests = hasPermission(
    workspace.myPermissions,
    Permission.WsUpdateResources
  )
  const selectedPaymentRequestId = searchParams.get("paymentRequestId")
  const isPaymentRequestDetailView =
    canManagePaymentRequests && Boolean(selectedPaymentRequestId)

  const handleFiltersChange = useCallback(
    (nextFilters: Partial<WorkspacePaymentRequestListFilters>) => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        ...nextFilters,
      }))
      setPage(1)
    },
    []
  )

  const handleSelectPaymentRequest = useCallback(
    (paymentRequestId: string) => {
      const nextSearchParams = new URLSearchParams(searchParams.toString())
      nextSearchParams.set("paymentRequestId", paymentRequestId)
      setSearchParams(nextSearchParams)
    },
    [searchParams, setSearchParams]
  )

  const handleBackToList = useCallback(() => {
    const nextSearchParams = new URLSearchParams(searchParams.toString())
    nextSearchParams.delete("paymentRequestId")
    setSearchParams(nextSearchParams)
  }, [searchParams, setSearchParams])

  return (
    <div className="flex flex-col space-y-8">
      {!isPaymentRequestDetailView ? (
        <div>
          <h3 className="text-2xl font-semibold leading-none tracking-tight">
            {t("workspace.tabs.payments")}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("settings.payments.description")}
          </p>
        </div>
      ) : null}

      {!isPaymentRequestDetailView ? (
        <WorkspacePaymentGatewaySection workspace={workspace} />
      ) : null}

      {canManagePaymentRequests ? (
        <>
          {!isPaymentRequestDetailView ? <div className="border-t" /> : null}
          {isPaymentRequestDetailView && selectedPaymentRequestId ? (
            <WorkspacePaymentRequestDetail
              workspace={workspace}
              paymentRequestId={selectedPaymentRequestId}
              onBack={handleBackToList}
            />
          ) : (
            <WorkspacePaymentRequestList
              workspace={workspace}
              filters={filters}
              page={page}
              onPageChange={setPage}
              onFiltersChange={handleFiltersChange}
              onSelectPaymentRequest={handleSelectPaymentRequest}
            />
          )}
        </>
      ) : null}
    </div>
  )
}

export default WorkspacePaymentsTab
